import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Star, MessageCircle, Send, Image as ImageIcon, X, Upload, Camera } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { imgUrl } from "@/utils/format";
import PhotoLightbox from "@/components/PhotoLightbox";
import { toast } from "sonner";

function StarRow({ value }) {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= value ? "fill-[#F4C430] text-[#F4C430]" : "text-[#E5E1D8]"}`} />
      ))}
    </div>
  );
}

function ReviewRow({ review, onReplied }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(review.reply || "");
  const [photos, setPhotos] = useState(review.reply_photos || []);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { photos, index }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > 4) {
      toast.error(t("orm.max4"));
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await apiClient.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, data.url]);
      }
      toast.success(t("orm.photosAdded", { n: files.length }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((p) => p !== url));

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 1) return;
    setLoading(true);
    try {
      const { data } = await apiClient.post(`/my/restaurant/reviews/${review.review_id}/reply`, { reply: text.trim(), photos });
      toast.success(t("orm.sentForModeration"));
      setOpen(false);
      onReplied?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`owner-review-${review.review_id}`} className="bg-white border border-sand rounded-2xl p-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-elevated grid place-items-center font-display font-semibold">
            {review.user_name?.[0]?.toUpperCase() || "C"}
          </div>
          <div>
            <div className="font-semibold text-sm">{review.user_name}</div>
            <div className="text-xs text-mute">{new Date(review.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
          </div>
        </div>
        <StarRow value={review.rating} />
      </div>
      {review.comment && <p className="text-sm text-ink mt-2 leading-relaxed">{review.comment}</p>}
      {review.photos?.length > 0 && (
        <div className="mt-2 flex gap-2 flex-wrap">
          {review.photos.map((p, i) => (
            <button key={i} type="button" onClick={() => setLightbox({ photos: review.photos, index: i })} data-testid={`owner-client-photo-${review.review_id}-${i}`} aria-label={t("orm.zoomPhoto", { n: i + 1 })}>
              <img src={imgUrl(p)} alt={`photo client ${i+1}`} className="w-28 h-28 rounded-xl object-cover hover:opacity-90 transition-opacity cursor-zoom-in" />
            </button>
          ))}
        </div>
      )}

      {review.reply && !open && (
        <div className={`mt-3 ml-4 pl-3 border-l-2 rounded-r-xl p-3 ${review.reply_status === "rejected" ? "border-[#E32636] bg-[#FDECEE]" : review.reply_status === "pending" ? "border-[#F4C430] bg-[#FFF9E6]" : "border-brand-red bg-[#FFF5F6]"}`}>
          <div className="flex items-center gap-2 text-xs text-brand-red mb-1 flex-wrap">
            <MessageCircle className="w-3 h-3" />
            <span className="font-semibold">{review.reply_author || t("orm.yourReply")}</span>
            {review.reply_at && <span className="text-mute">· {new Date(review.reply_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}</span>}
            {review.reply_status === "pending" && (
              <span data-testid={`reply-status-pending-${review.review_id}`} className="text-[10px] uppercase tracking-widest bg-[#FFF3CD] text-[#946A00] px-2 py-0.5 rounded-full">{t("orm.pending")}</span>
            )}
            {review.reply_status === "approved" && (
              <span data-testid={`reply-status-approved-${review.review_id}`} className="text-[10px] uppercase tracking-widest bg-[#E7F6EE] text-brand-green px-2 py-0.5 rounded-full">{t("orm.published")}</span>
            )}
            {review.reply_status === "rejected" && (
              <span data-testid={`reply-status-rejected-${review.review_id}`} className="text-[10px] uppercase tracking-widest bg-[#FDECEE] text-[#E32636] px-2 py-0.5 rounded-full">{t("orm.rejected")}</span>
            )}
          </div>
          <p className="text-sm text-ink leading-relaxed">{review.reply}</p>
          {review.reply_status === "rejected" && review.reply_reject_reason && (
            <p className="text-xs text-[#E32636] mt-1">{t("orm.rejectReason")} {review.reply_reject_reason}</p>
          )}
          {review.reply_status === "rejected" && (
            <p className="text-xs text-mute mt-1">{t("orm.editResend")}</p>
          )}
          {review.reply_photos?.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {review.reply_photos.map((p, i) => (
                <button key={i} type="button" onClick={() => setLightbox({ photos: review.reply_photos, index: i })} data-testid={`owner-reply-photo-${review.review_id}-${i}`} aria-label={t("orm.zoomPhoto", { n: i + 1 })}>
                  <img src={imgUrl(p)} alt={`photo ${i+1}`} className="w-28 h-28 rounded-xl object-cover hover:opacity-90 transition-opacity cursor-zoom-in" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!open ? (
        <button onClick={() => setOpen(true)} data-testid={`reply-btn-${review.review_id}`} className="mt-3 text-sm text-brand-red font-semibold inline-flex items-center gap-1">
          <MessageCircle className="w-4 h-4" /> {review.reply ? t("orm.editReply") : t("orm.reply")}
        </button>
      ) : (
        <form onSubmit={submit} className="mt-3 space-y-3">
          <textarea
            data-testid={`reply-textarea-${review.review_id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={600}
            placeholder={t("orm.ph")}
            className="w-full bg-elevated rounded-xl px-3 py-2 outline-none text-sm"
            required
          />

          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={imgUrl(p)} alt="" className="w-20 h-20 rounded-lg object-cover" />
                  <button type="button" onClick={() => removePhoto(p)} className="absolute -top-1 -right-1 bg-ink text-white rounded-full p-1">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex gap-2 flex-wrap">
              <label data-testid={`reply-camera-${review.review_id}`} className={`inline-flex items-center gap-1.5 text-xs rounded-full bg-brand-red text-white px-3 py-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                <Camera className="w-3 h-3" /> {uploading ? t("orm.sending") : t("orm.photo")}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} disabled={photos.length >= 4} />
              </label>
              <label data-testid={`reply-upload-${review.review_id}`} className={`inline-flex items-center gap-1.5 text-xs rounded-full border border-sand px-3 py-2 cursor-pointer hover:bg-elevated ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                <Upload className="w-3 h-3" /> {t("orm.file")} ({photos.length}/4)
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleUpload} disabled={photos.length >= 4} />
              </label>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="text-sm rounded-full px-4 py-2 border border-sand">{t("orm.cancel")}</button>
              <button type="submit" disabled={loading} data-testid={`reply-submit-${review.review_id}`} className="text-sm inline-flex items-center gap-1 rounded-full bg-brand-red text-white px-4 py-2 font-semibold disabled:opacity-60">
                <Send className="w-3 h-3" /> {loading ? t("orm.submitting") : t("orm.publish")}
              </button>
            </div>
          </div>
        </form>
      )}
      {lightbox && (
        <PhotoLightbox photos={lightbox.photos} initialIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

export default function OwnerReviewsManager({ reviews, onUpdate }) {
  const { t } = useTranslation();
  const handleReplied = (updated) => onUpdate?.(updated);
  if (!reviews || reviews.length === 0) {
    return (
      <div className="bg-white border border-sand rounded-2xl p-6 text-center text-mute">
        {t("orm.empty")}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="owner-reviews-manager">
      {reviews.map((r) => (
        <ReviewRow key={r.review_id} review={r} onReplied={handleReplied} />
      ))}
    </div>
  );
}
